import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import AuthService from "../Service/auth.service";
import useUser from "../Hooks/useUser";
import { useLocation } from "react-router-dom";
import { TourStep } from "../Components/Tour";

interface TourState {
  run: boolean;
  stepIndex: number;
  steps: TourStep[];
  tourActive: boolean;
}

interface TourContextType {
  state: TourState;
  setState: (
    patch:
      | Partial<TourState>
      | ((previousState: TourState) => Partial<TourState>)
  ) => void;
  maintainTourState: () => void;
  hasSeenTour: () => boolean;
  startTour: () => Promise<void>;
  markTourSeen: () => Promise<void>;
  getCurrentStep: () => TourStep | null;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

const initialTourState: TourState = {
  run: false,
  stepIndex: 0,
  steps: [],
  tourActive: false,
};

export const TourProvider = ({ children }: { children: ReactNode }) => {
  const [state, setStateInternal] = useState<TourState>(initialTourState);
  // Local flag to immediately track when tour is marked as seen (avoids race conditions)
  const [tourMarkedAsSeen, setTourMarkedAsSeen] = useState(false);
  // Flag to track if initial setup is done and tour should start when ready
  const [pendingTourStart, setPendingTourStart] = useState(false);
  const { user } = useUser();
  const location = useLocation();

  const patchState: TourContextType["setState"] = (patch) => {
    setStateInternal((prev) => ({
      ...prev,
      ...(typeof patch === "function" ? patch(prev) : patch),
    }));
  };

  const hasSeenTour = (): boolean => {
    // Check local flag first (immediate)
    if (tourMarkedAsSeen) return true;
    // If user hasn't loaded yet, we don't know - return false to allow tour to potentially start
    if (!user) return false;
    // Non-admins don't see the tour
    if (user.role !== "admin") return true;
    // Check user's walkthrough completion status
    return !!user.isWalkthroughCompleted;
  };

  const markTourSeen = async () => {
    // Set local flag immediately to prevent race conditions on navigation
    setTourMarkedAsSeen(true);
    
    // Use the latest user from useUser if available
    const currentUser = user || AuthService.getInstance().getCurrentUser();
    if (!currentUser) return;
    if (!currentUser.isWalkthroughCompleted) {
      // Create a copy to avoid mutating React state directly
      const updatedUser = { ...currentUser, isWalkthroughCompleted: true };
      try {
        await AuthService.getInstance().updateCurrentUser(updatedUser);
      } catch (err) {
        console.error("Failed to mark walkthrough as completed:", err);
      }
    }
  };

  const startTour = async () => {
    // Don't start if user not loaded, not admin, already seen, or on pre-load/onboarding paths
    if (!user || user.role !== "admin") return;
    if (hasSeenTour() || location.pathname === "/pre-load" || location.pathname === "/user-onboarding") return;

    patchState({
      run: true,
      tourActive: true,
      stepIndex: 0,
    });
  };

  const maintainTourState = () => {
    // Only maintain tour state if it's actually active
    if (state.tourActive) {
      patchState({
        run: true,
        tourActive: true,
      });
    }
  };

  useEffect(() => {
    const handleInitialSetupDone = () => {
      // Set flag to indicate tour should start when conditions are ready
      // Don't call startTour directly as user may not be loaded yet (stale closure)
      setPendingTourStart(true);
    };

    AuthService.getInstance().subscribe(
      AuthService.EVENTS.INITIAL_SETUP_DONE,
      handleInitialSetupDone
    );

    return () => {
      AuthService.getInstance().unsubscribe(
        AuthService.EVENTS.INITIAL_SETUP_DONE,
        handleInitialSetupDone
      );
    };
  }, []);

  // Sync local flag with user's isWalkthroughCompleted when user loads
  useEffect(() => {
    if (user?.isWalkthroughCompleted) {
      setTourMarkedAsSeen(true);
    }
  }, [user?.isWalkthroughCompleted]);

  // Ensure tour starts when user is loaded and eligible
  // Also handles pendingTourStart flag set by INITIAL_SETUP_DONE event
  useEffect(() => {
    const canStartTour =
      user &&
      user.role === "admin" &&
      !tourMarkedAsSeen &&
      !user.isWalkthroughCompleted &&
      location.pathname !== "/pre-load" &&
      location.pathname !== "/user-onboarding";

    if (canStartTour) {
      startTour();
      if (pendingTourStart) {
        setPendingTourStart(false);
      }
    } else if (pendingTourStart && location.pathname !== "/pre-load" && location.pathname !== "/user-onboarding") {
      // If pendingTourStart is true but other conditions not met yet,
      // set a timeout to retry (handles race conditions with React Router navigation)
      const timer = setTimeout(() => {
        // Re-check conditions after a brief delay
        const currentUser = AuthService.getInstance().getCurrentUser();
        if (
          currentUser &&
          currentUser.role === "admin" &&
          !currentUser.isWalkthroughCompleted
        ) {
          patchState({
            run: true,
            tourActive: true,
            stepIndex: 0,
          });
          setPendingTourStart(false);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.pathname, tourMarkedAsSeen, pendingTourStart]);

  const getCurrentStep = (): TourStep | null => {
    if (
      state.steps &&
      state.steps.length > 0 &&
      state.stepIndex < state.steps.length
    ) {
      return state.steps[state.stepIndex];
    }
    return null;
  };

  const value: TourContextType = {
    state,
    setState: patchState,
    maintainTourState,
    hasSeenTour,
    startTour,
    markTourSeen,
    getCurrentStep,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

export const useTour = (): TourContextType => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return context;
};
